import React, {
  useEffect,
  useCallback,
} from 'react';
import {
  useNavigate,
  useLocation,
} from 'react-router';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import i18n from 'i18next';

import {
  getStatus,
} from 'redux-resource';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  Button,
  Select,
  Spinner,
  EntityIcon,
} from 'web-components';

import {
  Flex,
  icons,
  Text,
} from 'web-styled';

import {
  decodeParam,
  capitalizeFirstLetter,
} from 'web-utils';
import * as S from './styled';

interface Props {
  type: 'program' | 'workout' | 'document';
  groupId: number;
  resourceType: string;
}

const Templates = ({
  type,
  groupId,
  resourceType,
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = decodeParam(location.search);
  const isArchived = params.status === 'inactive';
  const optionsList = 'library';
  const mapState = useCallback(state => ({
    items: coreSelectors.getGroupItems(
      resourceType,
      groupId,
    )(state),
    searchOptions: coreSelectors.getSearchGroupOptions(
      groupId,
      resourceType,
      optionsList,
    )(state),
    getGroupEntityAssignments: getStatus(
      state,
      [
        `clientGroups.requests.get${capitalizeFirstLetter(resourceType)}Assignments.status`,
      ],
    ),
    assigningStatus: getStatus(
      state,
      [
        'groupAssignments.requests.assignGroup.status',
        'groupAssignments.requests.unAssignGroup.status',
      ],
      false,
    ),
  }), [groupId, resourceType]);

  const {
    items,
    searchOptions,
    assigningStatus,
    getGroupEntityAssignments,
  } = useSelector(mapState, shallowEqual);
  const templateType = type === 'program' ? 'ProgramTemplate' : 'WorkoutTemplate';

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      coreActions.requestGroupEntityAssignments({
        status: isArchived ? '/archived' : '',
        groupId,
        resourceType,
      }),
    );
  }, [groupId, resourceType, params.status]);

  const handleAssignGroup = (templateId) => {
    const listName = `${type}_${templateId}`;
    dispatch(
      coreActions.requestAssignGroup({
        id: groupId,
        payload: {
          template_id: templateId,
          template_type: templateType,
        },
        listName,
      }),
    );
  };

  const handleUnAssignGroup = (templateId) => {
    dispatch(
      coreActions.requestUnAssignGroup({
        id: groupId,
        payload: {
          template_id: templateId,
          template_type: templateType,
        },
      }),
    );
  };

  const selectProps = {
    program: {
      options: searchOptions,
      placeholder: i18n.t('search_assign.placeholder.programTemplates'),
    },
    workout: {
      options: searchOptions,
      placeholder: i18n.t('search_assign.placeholder.workoutTemplates'),
    },
  }[type];

  return (
    <>
      {getGroupEntityAssignments.succeeded && (
        <Flex
          column
          fillSpace
          width="100%"
        >
          {selectProps && (
            <S.Search>
              <Select
                {...selectProps}
                value=""
                isClearable
                menuPosition="fixed"
                onChange={(option) => {
                  if (option?.value) {
                    handleAssignGroup(option.value);
                  }
                }}
              />
              {selectProps.archive && (
                <Button
                  appearance="link"
                  onClick={() => {
                    navigate({
                      pathname: `/client_groups/${groupId}/${type}s`,
                      search: `?status=${isArchived ? 'active' : 'inactive'}`,
                    });
                  }}
                >
                  {`Show ${isArchived ? 'Active' : 'Archived'}`}
                </Button>
              )}
            </S.Search>
          )}

          {items.length ? items.map(item => (
            <S.Item
              key={item.id}
            >
              <Flex
                spacing={10}
                fillSpace
                align="center"
                justify="space-between"
              >
                <Flex
                  spacing={10}
                  align="center"
                  justify="center"
                >
                  <Flex
                    width="18px"
                    height="20px"
                    align="center"
                    justify="center"
                  >
                    <EntityIcon
                      resourceType={resourceType}
                      inactive={isArchived}
                    />
                  </Flex>
                  <S.Name
                    to={
                      {
                        workout: `/workout_templates/${item.id}/workout`,
                        program: `/program_templates/${item.id}/workouts`,
                        document: `/documents/${item.id}`,
                      }[type]
                    }
                    inactive={isArchived ? 1 : 0}
                  >
                    {item.name}
                  </S.Name>
                </Flex>
                <Flex
                  align="center"
                  className="actions"
                  spacing={10}
                >
                  {/*
                    <Button
                      ghost
                      spacing="compact"
                      appearance="primary"
                      onClick={() => (isArchived
                          ? onRequestRestore(item.id)
                        : onRequestArchive(item.id)
                      )}
                    >
                      {isArchived ? 'Restore' : 'Archive'}
                    </Button>
                  */}
                  {type !== 'document' && (
                    <Button
                      ghost
                      appearance="danger"
                      onClick={() => handleUnAssignGroup(item.id)}
                    >
                      Unassign
                    </Button>
                  )}
                </Flex>
              </Flex>
            </S.Item>
          )) : (
            <S.Empty>
              <S.EmptyIcon
                src={icons[`${type}sEmpty`]}
                alt=""
              />
              <Text whiteSpace="pre-line">
                {i18n.t(`client_groups.assigned.${type}.empty`)}
              </Text>
            </S.Empty>
          )}
        </Flex>
      )}

      {getGroupEntityAssignments.pending && (
        <Spinner
          message="Loading..."
        />
      )}

      {assigningStatus.pending && (
        <Spinner
          over
          message={`Assigning ${type}...`}
        />
      )}
    </>
  );
};

export default Templates;
